/**
 * NFC Solutions — Chime volume master multiplier (Session 27.67)
 *
 * A single 0.0-1.0 multiplier that scales every chime's peak gain.
 * Stored per device in localStorage so a smaller bar can turn the
 * loud kitchen/bar chime down to 40% without patching code, while a
 * huge open-air terrace can push it to 100%. Applies globally to:
 *   * playKitchenChime()
 *   * playBarChime()
 *   * playStaffChime()
 *   * playOrderReadyChime()
 *   * playCoordinatorHandoverChime()
 *   * playLoginChime()
 *
 * Design decisions:
 *   * ONE master, not per-chime. Managers rarely want the kitchen
 *     chime loud AND the order-ready ping quiet on the same tablet —
 *     the natural mental model is "how loud is this tablet".
 *   * Stored in localStorage (not IDB) so it survives WebView data
 *     purges the same way `nfcs:install:completed_at_backup` does.
 *   * Default 1.0 (100%) so devices that never open the slider keep
 *     the SAME behaviour as before 27.67 — no silent regression.
 *   * Never lower than 0.0 or higher than 1.0. Values above 1.0
 *     don't make the chime louder (Web Audio API clips at 1.0
 *     regardless), they just add distortion.
 */

const STORAGE_KEY = "nfcs:chime:volume_multiplier";
const DEFAULT_MULTIPLIER = 1.0;

let _cached = null;
const _listeners = new Set();

function _readFromStorage() {
  try {
    if (typeof localStorage === "undefined") return DEFAULT_MULTIPLIER;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return DEFAULT_MULTIPLIER;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_MULTIPLIER;
    return Math.max(0, Math.min(1, n));
  } catch (_) {
    return DEFAULT_MULTIPLIER;
  }
}

/** Read the current multiplier (0.0-1.0). Cached in memory so
 * repeated calls from _runChimeNow don't touch localStorage on
 * every tone scheduled. */
export function getChimeVolume() {
  if (_cached == null) _cached = _readFromStorage();
  return _cached;
}

/** Write a new multiplier and notify subscribers so any open volume
 * slider in the UI updates immediately. Clamps to [0, 1]. */
export function setChimeVolume(value) {
  const clamped = Math.max(0, Math.min(1, Number(value) || 0));
  _cached = clamped;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, String(clamped));
    }
  } catch (_) { /* localStorage may be unavailable in private mode */ }
  for (const fn of _listeners) {
    try { fn(clamped); } catch (_) { /* noop */ }
  }
  return clamped;
}

/** Return the master volume as an integer 0-100 for UI display. */
export function getChimeVolumePercent() {
  return Math.round(getChimeVolume() * 100);
}

/** Set the master volume from a 0-100 UI slider value. */
export function setChimeVolumePercent(percent) {
  return setChimeVolume(Math.max(0, Math.min(100, Number(percent) || 0)) / 100);
}

/** Subscribe to volume changes. Returns an unsubscribe function.
 * Called by the volume-slider popovers so the displayed value stays
 * in sync when multiple sliders are open (e.g. kitchen + staff on
 * different dashboards). */
export function subscribeChimeVolume(handler) {
  if (typeof handler !== "function") return () => {};
  _listeners.add(handler);
  // Fire once with the current value so the subscriber renders in
  // the correct state without an extra effect.
  try { handler(getChimeVolume()); } catch (_) { /* noop */ }
  return () => { _listeners.delete(handler); };
}
